import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { assayApi, BioactivitySummary } from '../../../services/assayApi';
import apiClient from '../../../api/client';

interface BioactivityTabProps {
  moleculeId?: number;
  smiles?: string;
}

/** Guard: ensure value is a positive integer (not NaN, not a string, not 0). */
const isValidId = (v: unknown): v is number =>
  typeof v === 'number' && Number.isFinite(v) && Number.isInteger(v) && v > 0;

const BioactivityTab: React.FC<BioactivityTabProps> = ({ moleculeId, smiles }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<BioactivitySummary[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [fetched, setFetched] = useState(false);
  const [resolvedMoleculeId, setResolvedMoleculeId] = useState<number | undefined>(
    isValidId(moleculeId) ? moleculeId : undefined,
  );

  // Reset state when the molecule changes
  useEffect(() => {
    setFetched(false);
    setData([]);
    setError(null);
    setResolvedMoleculeId(isValidId(moleculeId) ? moleculeId : undefined);
  }, [moleculeId, smiles]);

  // Resolve SMILES to moleculeId if not provided directly
  useEffect(() => {
    if (isValidId(moleculeId)) { setResolvedMoleculeId(moleculeId); return; }
    if (!smiles) return;
    // Try to find this molecule in user's projects
    apiClient.get('/search/find-in-projects', { params: { smiles } })
      .then((resp) => {
        const matches = resp.data.matches || [];
        if (matches.length > 0 && isValidId(matches[0].molecule_id)) {
          setResolvedMoleculeId(matches[0].molecule_id);
        }
      })
      .catch(() => {});
  }, [moleculeId, smiles]);

  // Fetch bioactivity data
  useEffect(() => {
    if (!resolvedMoleculeId || fetched) return;
    if (!isValidId(resolvedMoleculeId)) return;

    let cancelled = false;
    const timeout = setTimeout(() => {
      if (!cancelled) {
        setLoading(false);
        setError('Request timed out');
        setFetched(true);
      }
    }, 10000);

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        let results = await assayApi.getMoleculeBioactivity(resolvedMoleculeId);

        // Fallback: if direct ID returned nothing and we have SMILES, try SMILES resolution
        if (results.length === 0 && smiles && resolvedMoleculeId === moleculeId) {
          try {
            const resp = await apiClient.get('/search/find-in-projects', { params: { smiles } });
            const matches = resp.data.matches || [];
            const altId = matches.find((m: any) => isValidId(m.molecule_id) && m.molecule_id !== moleculeId)?.molecule_id;
            if (altId) {
              results = await assayApi.getMoleculeBioactivity(altId);
            }
          } catch { /* ignore resolution failure */ }
        }

        if (!cancelled) { setData(results); setFetched(true); }
      } catch (err: any) {
        if (!cancelled) {
          const status = err?.response?.status;
          const detail = err?.response?.data?.detail;
          const msg = status === 422
            ? 'Invalid molecule identifier'
            : typeof detail === 'string'
              ? detail
              : 'Unable to load bioactivity data';
          setError(msg);
          setData([]);
          setFetched(true);
        }
      } finally {
        clearTimeout(timeout);
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => { cancelled = true; clearTimeout(timeout); };
  }, [resolvedMoleculeId, fetched, smiles, moleculeId]);

  const handleRetry = useCallback(() => {
    setFetched(false);
    setError(null);
    setData([]);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-5 h-5 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: 'var(--primary-600)', borderTopColor: 'transparent' }} />
        <span className="ml-2 text-sm" style={{ color: 'var(--text-muted)' }}>Loading bioactivity data...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-3">
        <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: 'rgba(239,68,68,0.1)' }}>
          <svg className="w-6 h-6" style={{ color: 'var(--error-500, #EF4444)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
              d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
            />
          </svg>
        </div>
        <p className="text-sm" style={{ color: 'var(--text-primary)' }}>Unable to load bioactivity data</p>
        <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{error}</p>
        <button
          onClick={handleRetry}
          className="mt-1 px-3 py-1.5 text-xs font-medium rounded-md"
          style={{
            backgroundColor: 'var(--primary-600)',
            color: '#fff',
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  if (!resolvedMoleculeId && !loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-3">
        <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
          {smiles ? 'This compound is not in your projects — no bioactivity data available' : 'No molecule selected'}
        </p>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-3">
        <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: 'var(--hover-bg)' }}>
          <svg className="w-6 h-6" style={{ color: 'var(--text-muted)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
              d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z"
            />
          </svg>
        </div>
        <p className="text-sm" style={{ color: 'var(--text-muted)' }}>No bioactivity data for this molecule</p>
        <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Import assay results to see data here</p>
      </div>
    );
  }

  // Group by target
  const byTarget: Record<string, BioactivitySummary[]> = {};
  for (const item of data) {
    const key = item.target_name || 'Unknown';
    if (!byTarget[key]) byTarget[key] = [];
    byTarget[key].push(item);
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-xs font-medium px-2 py-0.5 rounded" style={{
          backgroundColor: 'rgba(13, 148, 136, 0.1)',
          color: 'var(--primary-600)',
        }}>
          {data.length} result{data.length !== 1 ? 's' : ''}
        </span>
        <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
          across {Object.keys(byTarget).length} target{Object.keys(byTarget).length !== 1 ? 's' : ''}
        </span>
      </div>

      {Object.entries(byTarget).map(([target, items]) => (
        <div key={target} className="rounded-lg overflow-hidden" style={{
          border: '1px solid var(--card-border)',
          backgroundColor: 'var(--card-bg)',
        }}>
          <div className="px-3 py-2 flex items-center gap-2" style={{
            borderBottom: '1px solid var(--card-border)',
            backgroundColor: 'var(--hover-bg)',
          }}>
            <span className="text-xs font-semibold" style={{ color: 'var(--primary-600)' }}>
              {target}
            </span>
            <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
              {items.length} assay{items.length !== 1 ? 's' : ''}
            </span>
          </div>

          <table className="w-full text-xs" style={{ fontFamily: 'var(--font-mono)' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--card-border)' }}>
                <th className="text-left px-3 py-1.5 font-medium" style={{ color: 'var(--text-muted)' }}>Assay</th>
                <th className="text-left px-3 py-1.5 font-medium" style={{ color: 'var(--text-muted)' }}>Type</th>
                <th className="text-right px-3 py-1.5 font-medium" style={{ color: 'var(--text-muted)' }}>Value</th>
                <th className="text-left px-3 py-1.5 font-medium" style={{ color: 'var(--text-muted)' }}>Unit</th>
                <th className="text-right px-3 py-1.5 font-medium" style={{ color: 'var(--text-muted)' }}>Hill</th>
                <th className="text-right px-3 py-1.5 font-medium" style={{ color: 'var(--text-muted)' }}>R²</th>
                <th className="text-left px-3 py-1.5 font-medium" style={{ color: 'var(--text-muted)' }}>Date</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, i) => {
                // Flag dangerous values (e.g. hERG < 10 µM)
                const isHergDanger = target.toLowerCase() === 'herg' && item.value < 10;
                const valueColor = isHergDanger ? 'var(--error-500)' : 'var(--text-primary)';
                const registryAssayId = isValidId(item.registry_assay_id)
                  ? item.registry_assay_id
                  : null;

                return (
                  <tr
                    key={i}
                    className={registryAssayId ? 'hover:opacity-80 cursor-pointer' : undefined}
                    style={{ borderBottom: '1px solid var(--card-border)' }}
                    title={registryAssayId ? 'Open registered assay' : 'Imported reference result — not linked to a registered assay'}
                    onClick={registryAssayId ? () => navigate(`/biology/assays/${registryAssayId}`) : undefined}
                  >
                    <td className="px-3 py-1.5" style={{ color: 'var(--primary-600)' }}>
                      <div>{item.assay_name}</div>
                      {!registryAssayId && (
                        <div className="text-[10px] mt-0.5" style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-sans)' }}>
                          Imported reference · no registered assay link
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-1.5" style={{ color: 'var(--text-secondary)' }}>
                      {item.assay_type || '-'}
                    </td>
                    <td className="px-3 py-1.5 text-right font-semibold" style={{ color: valueColor }}>
                      {item.qualifier !== '=' ? item.qualifier : ''}{item.value.toFixed(1)}
                    </td>
                    <td className="px-3 py-1.5" style={{ color: 'var(--text-secondary)' }}>
                      {item.unit || '-'}
                    </td>
                    <td className="px-3 py-1.5 text-right" style={{ color: 'var(--text-secondary)' }}>
                      {item.hill_slope?.toFixed(2) || '-'}
                    </td>
                    <td className="px-3 py-1.5 text-right" style={{ color: 'var(--text-secondary)' }}>
                      {item.r_squared?.toFixed(2) || '-'}
                    </td>
                    <td className="px-3 py-1.5" style={{ color: 'var(--text-muted)' }}>
                      {item.assay_date || '-'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
};

export default BioactivityTab;
